/**
 * Display formatters shared across pages.
 *
 * Returns `null` for missing/invalid values so callers can render their
 * own placeholder (typically <Empty /> from components/States.tsx).
 */
export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

/**
 * Compact inclusive date range, e.g. "15–16 Jun 2026" (same month),
 * "30 Jun – 2 Jul 2026" (same year), or two full dates across years. Used
 * for a multi-day shoot's span on the timeline. Collapses to a single
 * `formatDate` when both ends are the same day.
 */
export function formatDateRange(
  fromIso: string | null | undefined,
  toIso: string | null | undefined,
): string | null {
  if (!fromIso) return formatDate(toIso);
  if (!toIso) return formatDate(fromIso);
  const a = new Date(fromIso);
  const b = new Date(toIso);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) {
    return formatDate(fromIso) ?? formatDate(toIso);
  }
  const part = (d: Date, opts: Intl.DateTimeFormatOptions) =>
    d.toLocaleDateString(undefined, opts);
  const day = (d: Date) => part(d, { day: '2-digit' });
  const mon = (d: Date) => part(d, { month: 'short' });
  const yr = (d: Date) => part(d, { year: 'numeric' });
  if (formatDate(fromIso) === formatDate(toIso)) return formatDate(fromIso);
  if (yr(a) === yr(b) && mon(a) === mon(b)) {
    return `${day(a)}–${day(b)} ${mon(b)} ${yr(b)}`;
  }
  if (yr(a) === yr(b)) {
    return `${day(a)} ${mon(a)} – ${day(b)} ${mon(b)} ${yr(b)}`;
  }
  return `${formatDate(fromIso)} – ${formatDate(toIso)}`;
}

/**
 * Date with the weekday abbreviation in front, e.g. "Tue, 2 Jun 2026".
 * Used in the shoot-day schedule where the day of the week is part of
 * planning a shoot. Kept separate from `formatDate` so the weekday only
 * shows where it matters, not on every date across the app.
 */
export function formatDateWithWeekday(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export function formatRelativeDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const now = Date.now();
  const diffMs = d.getTime() - now;
  const days = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (Math.abs(days) < 1) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days === -1) return 'Yesterday';
  if (days > 0 && days < 14) return `in ${days} days`;
  if (days < 0 && days > -14) return `${Math.abs(days)} days ago`;
  return formatDate(iso);
}

export function classNames(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/** "just now" / "6 min ago" / "3 h ago" / "2 days ago" — for freshness
 *  stamps like "Xero synced 6 min ago", where a calendar date is too
 *  coarse to be reassuring. */
export function formatTimeAgo(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const mins = Math.max(0, Math.round((Date.now() - d.getTime()) / 60_000));
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}
